import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Flame, Zap, Trophy, Bookmark, Briefcase, Lightbulb, ArrowRight, ShieldCheck } from "lucide-react";

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [tip, setTip] = useState(null);

  useEffect(() => {
    api.get("/dashboard/me").then((r) => setData(r.data));
    api.get("/daily/tip").then((r) => setTip(r.data.tip));
  }, []);

  const trialDaysLeft = () => {
    if (user?.subscription_status !== "trial" || !user.trial_ends_at) return null;
    const ms = new Date(user.trial_ends_at) - new Date();
    return Math.max(0, Math.ceil(ms / (1000 * 60 * 60 * 24)));
  };
  const daysLeft = trialDaysLeft();

  const stat = (label, value, Icon) => (
    <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4">
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-widest text-slate-500">{label}</div>
        <Icon className="h-4 w-4 text-amber-500" />
      </div>
      <div className="mt-2 text-2xl font-extrabold mono">{value}</div>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500">Welcome back</div>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight">Hey {user?.name?.split(" ")[0] || "there"} 👋</h1>
          <p className="text-slate-600 dark:text-slate-400 mt-1">Keep learning — your safety career depends on it.</p>
        </div>
        <div className="flex items-center gap-2">
          {user?.is_premium ? (
            <div className="pill bg-emerald-500/15 text-emerald-600 dark:text-emerald-400"><ShieldCheck className="h-3 w-3" /> Premium</div>
          ) : (
            <Link data-testid="upgrade-btn" to="/subscription" className="btn-accent !py-2 !px-4 text-sm">Upgrade</Link>
          )}
          {daysLeft !== null && (
            <div className="pill bg-amber-500/15 text-amber-600 dark:text-amber-400" data-testid="trial-countdown">
              Trial ends in {daysLeft} day{daysLeft === 1 ? "" : "s"}
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {stat("XP", user?.xp ?? 0, Zap)}
        {stat("Streak", `${user?.streak ?? 0}d`, Flame)}
        {stat("Best Score", `${data?.best_score ?? 0}%`, Trophy)}
        {stat("Saved Jobs", data?.saved_jobs_count ?? 0, Briefcase)}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-semibold">Recent exam attempts</h3>
            <Link to="/exams" className="text-xs font-semibold text-amber-600">All exams <ArrowRight className="inline h-3 w-3" /></Link>
          </div>
          {data?.recent_attempts?.length ? (
            <ul className="divide-y divide-slate-200 dark:divide-slate-800">
              {data.recent_attempts.map((a) => (
                <li key={a.attempt_id} className="py-3 flex items-center justify-between">
                  <div>
                    <div className="text-sm font-semibold">{a.exam_title}</div>
                    <div className="text-xs text-slate-500">{new Date(a.created_at).toLocaleString()}</div>
                  </div>
                  <div className="text-right">
                    <div className={`mono font-bold ${a.passed ? "text-emerald-500" : "text-rose-500"}`}>{a.percentage}%</div>
                    <div className="text-[10px] uppercase tracking-widest">{a.passed ? "Passed" : "Failed"}</div>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <div className="text-sm text-slate-500">No attempts yet. <Link to="/exams" className="text-amber-600 font-semibold">Start your first mock →</Link></div>
          )}
        </div>

        <div className="rounded-2xl border border-amber-500/40 bg-amber-500/5 dark:bg-amber-500/10 p-5">
          <div className="flex items-center gap-2 mb-2">
            <Lightbulb className="h-4 w-4 text-amber-500" />
            <div className="text-xs uppercase tracking-widest text-amber-700 dark:text-amber-400">Daily safety tip</div>
          </div>
          {tip ? (
            <>
              <h4 className="text-lg font-semibold">{tip.title}</h4>
              <p className="mt-1 text-sm text-slate-700 dark:text-slate-300">{tip.tip}</p>
            </>
          ) : <div className="text-sm text-slate-500">Loading…</div>}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Link to="/learn" data-testid="cta-learn" className="rounded-2xl border border-slate-200 dark:border-slate-800 p-5 hover:border-amber-500 transition-colors">
          <div className="text-xs uppercase tracking-widest text-slate-500">Continue learning</div>
          <div className="mt-2 font-semibold">HSE Digital Library</div>
          <div className="text-sm text-slate-500">50+ topics across construction, environmental & general HSE.</div>
        </Link>
        <Link to="/tools/interview" data-testid="cta-interview" className="rounded-2xl border border-slate-200 dark:border-slate-800 p-5 hover:border-amber-500 transition-colors">
          <div className="text-xs uppercase tracking-widest text-slate-500">Interview prep</div>
          <div className="mt-2 font-semibold">Practice interview questions</div>
          <div className="text-sm text-slate-500">Role-based Q&A with model answers.</div>
        </Link>
        <Link to="/jobs" data-testid="cta-jobs" className="rounded-2xl border border-slate-200 dark:border-slate-800 p-5 hover:border-amber-500 transition-colors">
          <div className="text-xs uppercase tracking-widest text-slate-500">Careers</div>
          <div className="mt-2 font-semibold">Find HSE jobs</div>
          <div className="text-sm text-slate-500">Save, track and apply to roles across the region.</div>
        </Link>
      </div>
    </div>
  );
}
